import {
  AnimatePresence,
  domAnimation,
  LazyMotion,
  m,
  type Variants,
} from 'motion/react';
import type { ProductDisplayFields, StoreConfig } from './services/types';
import { ProductTile } from './ProductTile';
import { useEffect, useMemo, useRef } from 'react';
import ReactPaginate from 'react-paginate';
import { IconChevronRight, IconChevronLeft } from '@tabler/icons-react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useMediaQuery } from '@mantine/hooks';
import { useHeaderTracking } from './hooks/useHeaderTracking';
import { useAnalytics } from './hooks/useAnalytics';
import { AnalyticsEvent } from './constants/analyticsEvents';
import { useChatContext } from './context/use-chat-context';

const container: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1,
      type: 'tween',
      duration: 0.4,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: {
      duration: 0.5,
      ease: [0.32, 0.72, 0, 1],
      staggerChildren: 0.03,
      staggerDirection: -1,
      when: 'afterChildren',
    },
  },
};

interface Props {
  products: ProductDisplayFields[];
  storeConfig?: StoreConfig;
  currentPage: number;
  totalPages?: number;
  loading: boolean;
  onPageChange: (page: number) => void;
}

export enum GalleryOrientation {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export const ProductGallery = ({
  products,
  currentPage,
  totalPages,
  storeConfig,
  loading,
  onPageChange,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const scrollableDivRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { topStyle } = useHeaderTracking({
    storeConfig,
  });

  const { setProductGalleryScrollPosition } = useChatContext();

  const { trackEvent } = useAnalytics();

  const handlePageClick = (event: { selected: number }) => {
    onPageChange(event.selected + 1);
    scrollableDivRef?.current?.scroll({
      top: 0,
      behavior: 'smooth',
    });
  };

  const galleryOrientation =
    storeConfig?.galleryOrientation || GalleryOrientation.HORIZONTAL;
  const isGalleryHorizontal =
    galleryOrientation === GalleryOrientation.HORIZONTAL;

  const isVisible = useMemo(() => {
    return products?.length || loading;
  }, [products, loading]);

  useEffect(() => {
    if (!loading && products?.length) {
      trackEvent(AnalyticsEvent.PRODUCTS_GALLERY_VIEWED);
    }
  }, [loading, trackEvent, products]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setProductGalleryScrollPosition(scrollTop);
  };

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        {isVisible && (
          <m.div
            id="caddie-product-gallery"
            key="caddie-product-gallery"
            initial="hidden"
            animate="show"
            exit="exit"
            variants={container}
            ref={ref}
            style={{
              top: topStyle,
              position: 'fixed',
            }}
            className={
              'caddietw-flex caddietw-flex-row caddietw-fixed caddietw-top-0 caddietw-h-[100vh] caddietw-w-[100vw] caddietw-z-10 caddietw-left-0 caddietw-overflow-y-auto caddietw-bg-white caddietw-justify-center caddietw-pt-6 caddietw-pointer-events-auto'
            }
          >
            <div
              ref={scrollableDivRef}
              onScroll={handleScroll}
              className={
                'caddie-product-gallery-root caddietw-flex caddietw-flex-row caddietw-pt-[46px] caddietw-justify-items-center caddietw-bg-white caddietw-rounded-[12px] md:caddietw-pb-[120px] caddietw-w-full caddietw-h-full caddietw-justify-center caddietw-overflow-y-auto no-scrollbar caddietw-px-5 lg:caddietw-px-12 caddietw-max-w-[1920px]'
              }
            >
              <div
                className={`caddie-product-gallery-sub-root caddietw-w-full ${
                  isGalleryHorizontal
                    ? 'caddietw-grid caddietw-grid-cols-2 sm:caddietw-grid-cols-2 md:caddietw-grid-cols-3 lg:caddietw-grid-cols-4 caddietw-gap-2 md:caddietw-gap-6 lg:caddietw-gap-8'
                    : 'caddietw-flex caddietw-flex-col caddietw-items-center'
                } md:caddietw-justify-items-center caddietw-pb-[60px] md:caddietw-pb-[60px] caddietw-h-fit`}
              >
                {loading &&
                  Array.from({ length: 8 }).map((_, index) => {
                    return (
                      <div
                        key={`${index}`}
                        className={`caddietw-flex ${
                          isGalleryHorizontal
                            ? 'caddietw-flex-col caddietw-justify-between caddietw-w-[full] caddietw-gap-5'
                            : 'caddietw-w-[70%] caddietw-gap-10 caddietw-items-start'
                        } caddietw-h-auto caddietw-box-border caddietw-p-4`}
                      >
                        <div
                          className={`caddietw-w-[240px] caddietw-m-w-[240px] md:caddietw-w-[240px] caddietw-min-h-[280px] ${
                            isGalleryHorizontal
                              ? 'caddietw-w-full'
                              : 'caddietw-w-full caddietw-h-[280px]'
                          }`}
                        >
                          <Skeleton className="caddietw-rounded-md caddietw-w-full caddietw-h-full" />
                        </div>
                        <div>
                          <div className="caddietw-h-auto caddietw-w-full">
                            <div className={'caddietw-w-[25%]'}>
                              <Skeleton className="caddietw-text-[#4BABA1] caddietw-text-sm caddietw-font-semibold" />
                            </div>

                            <div className="caddietw-flex caddietw-flex-col caddietw-justify-between caddietw-mt-2 caddietw-w-[50%]">
                              <Skeleton className="caddietw-font-semibold caddietw-text-black caddietw-text-sm caddietw-w-full" />
                            </div>
                          </div>
                          <div
                            className={`caddietw-w-full caddietw-mt-2 caddietw-flex ${
                              isGalleryHorizontal
                                ? 'caddietw-h-auto'
                                : 'caddietw-h-[210px] caddietw-items-end'
                            }`}
                          >
                            <div className={'caddietw-mb-2 caddietw-w-[240px]'}>
                              <Skeleton className="caddietw-font-bold caddietw-text-sm caddietw-text-[#FF4233]" />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                {!loading &&
                  products?.map((product, index) => {
                    const productDetails = product;

                    return (
                      <div
                        key={`${productDetails.slug}-${index}`}
                        className={`superbar-product-tile-container caddietw-box-border caddietw-flex caddietw-w-full ${
                          isGalleryHorizontal
                            ? ''
                            : 'lg:caddietw-w-[800px] lg:caddietw-min-h-[170px] caddietw-border-0 caddietw-border-b caddietw-border-solid caddietw-border-[#D5D5D5]'
                        } caddietw-justify-center`}
                      >
                        <ProductTile
                          productId={productDetails.slug}
                          currency={storeConfig?.localization?.currency || '£'}
                          price={productDetails.regularPrice}
                          salePrice={productDetails.salePrice}
                          title={productDetails.name as string}
                          description={productDetails.description as string}
                          imageUrl={`${productDetails.images?.[0] as string}`}
                          productUrl={`/${product.slug}`}
                          productRange={productDetails.parentName as string}
                          decimalPlaces={
                            storeConfig?.localization?.decimalPlaces
                          }
                          brandColor={storeConfig?.localization?.brandColor}
                          galleryOrientation={galleryOrientation}
                        />
                      </div>
                    );
                  })}
                <div
                  key={'pagination'}
                  className={
                    'caddietw-col-span-2 sm:caddietw-col-span-2 md:caddietw-col-span-3 lg:caddietw-col-span-4 caddietw-flex caddietw-flex-row caddietw-justify-center caddietw-p-4 caddietw-pb-[320px] md:caddietw-pb-56 caddietw-pt-[60px]'
                  }
                >
                  <ReactPaginate
                    containerClassName={`caddietw-flex caddietw-flex-row caddietw-justify-center caddietw-w-full ${
                      loading
                        ? 'caddietw-pointer-events-none caddietw-filter caddietw-grayscale'
                        : ''
                    }`}
                    pageClassName={
                      'caddietw-h-auto caddietw-h-auto caddietw-flex caddietw-items-center'
                    }
                    pageLinkClassName={
                      'caddietw-p-4 caddietw-mx-1 caddietw-cursor-pointer caddietw-text-[#4BABA1] caddietw-font-semibold caddietw-text-sm caddietw-border caddietw-border-[#4BABA1] caddietw-rounded-md'
                    }
                    activeLinkClassName={
                      'caddietw-bg-[#4BABA1] caddietw-text-white caddietw-font-semibold caddietw-rounded-md caddietw-p-4'
                    }
                    previousClassName={
                      'caddietw-h-auto caddietw-h-auto caddietw-flex caddietw-items-center'
                    }
                    nextClassName={
                      'caddietw-h-auto caddietw-h-auto caddietw-flex caddietw-items-center'
                    }
                    breakLabel="..."
                    breakLinkClassName={
                      'caddietw-p-4 caddietw-mx-1 caddietw-cursor-pointer caddietw-text-[#4BABA1] caddietw-font-semibold caddietw-text-sm'
                    }
                    breakClassName={
                      'caddietw-h-auto caddietw-h-auto caddietw-flex caddietw-items-center'
                    }
                    nextLabel={
                      <IconChevronRight
                        size={32}
                        stroke={2}
                        color={'#4BABA1'}
                      />
                    }
                    onPageChange={handlePageClick}
                    forcePage={currentPage - 1}
                    pageRangeDisplayed={isMobile ? 1 : 4}
                    marginPagesDisplayed={isMobile ? 1 : 3}
                    pageCount={totalPages || 1}
                    previousLabel={
                      <IconChevronLeft size={32} stroke={2} color={'#4BABA1'} />
                    }
                    renderOnZeroPageCount={null}
                  />
                </div>
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
};
